import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EmployeeUser, User } from '../types/users';
import { Observable } from 'rxjs';
import { IApiBackendRouteService } from '../abstract/api-routes.service';
import { API_ENDPOINTS } from '../../../core/providers/constants/api.constants';
import { Role } from '../types/roles';
import { ApiRouteUserDto } from '../dto/user.dto';
import { ApiRouteTemporaryAssignationDto } from '../dto/temporary-assignation.dto';
import { ValidScrapOperator } from 'src/app/views/scheduler-stage/interfaces/valid-scrap-operator.interface';

@Injectable({
  providedIn: 'root',
})
export class ApiRouteUserService extends IApiBackendRouteService {
  constructor(protected readonly http: HttpClient) {
    super();
  }

  urlRoute = `${API_ENDPOINTS.USERS}`;

  getAllUsersMinimized(): Observable<User[]> {
    return this.http.get<User[]>(`${this.urlRoute}/minimized/active`);
  }

  allUsersV2(department: string = '', role: string = ''): Observable<User[]> {
    let query = new HttpParams();
    if (department) {
      query = query.set('department', `${department}`);
    }
    if (role) {
      query = query.set('role', `${role}`);
    }
    return this.http.get<User[]>(`${this.urlRoute}/v2?${query.toString()}`);
  }

  getUsers(
    filters: ApiRouteUserDto['findUsers']['body']
  ): Observable<EmployeeUser[]> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value != null && value !== '') {
        params = params.set(key, value.toString());
      }
    });
    return this.http.get<EmployeeUser[]>(this.urlRoute, { params });
  }

  getUser(userId: number): Observable<User> {
    return this.http.get<User>(`${this.urlRoute}/${userId}`);
  }

  getUserRoles(userId: number): Observable<Role[]> {
    return this.http.get<Role[]>(`${this.urlRoute}/${userId}/roles`);
  }

  getEmailRecipients(): Observable<User[]> {
    return this.http.get<User[]>(`${this.urlRoute}/email-recipients`);
  }

  getOperator(employeeNumber: number): Observable<User> {
    return this.http.get<User>(`${this.urlRoute}/number/${employeeNumber}`);
  }

  getSeveralOperators(ids: number[]): Observable<User[]> {
    const query = new URLSearchParams({});
    let endpoint = `${this.urlRoute}/get-several`;
    ids.forEach((userId) => {
      query.append('ids', userId.toString());
    });
    endpoint = `${endpoint}?${query.toString()}`;
    return this.http.get<User[]>(endpoint);
  }

  addUser(body: ApiRouteUserDto['createUser']['body']): Observable<User> {
    return this.http.post<User>(`${this.urlRoute}`, body);
  }

  transferUsers(
    body: ApiRouteUserDto['transferUsers']['body']
  ): Observable<any> {
    return this.http.post<any>(`${this.urlRoute}/transfer`, body);
  }

  updateUser(
    userId: number,
    body: ApiRouteUserDto['updateUser']['body']
  ): Observable<User> {
    return this.http.patch<User>(`${this.urlRoute}/${userId}`, body);
  }

  changeUserStatus(userId: number, status: boolean): Observable<User> {
    return this.http.patch<User>(`${this.urlRoute}/${userId}/status`, {
      active: status,
    });
  }

  changeSeveralUserStatus(
    userIds: number[],
    status: boolean
  ): Observable<User> {
    return this.http.post<User>(`${this.urlRoute}/status`, {
      active: status,
      userIds,
    });
  }

  deleteUser(userId: number): Observable<any> {
    return this.http.delete(`${this.urlRoute}/${userId}`);
  }

  // Temporary Assignation

  createTemporaryAssignation(body: ApiRouteTemporaryAssignationDto) {
    return this.http.post(`${this.urlRoute}/temporary-assignations`, body);
  }

  removeTemporaryAssignation(body: ApiRouteTemporaryAssignationDto) {
    return this.http.delete(`${this.urlRoute}/temporary-assignations`, {
      body,
    });
  }

  setToObsolete(ids: number[]): Observable<any> {
    return this.http.patch(`${this.urlRoute}/assignations/set-to-obsolete`, {
      ids,
    });
  }

  getValidOperators(
    transactionNumber: string
  ): Observable<ValidScrapOperator[]> {
    return this.http.get<ValidScrapOperator[]>(
      `${this.urlRoute}/valid-operators/${transactionNumber}`
    );
  }
}
